import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import reportHealthService from "../../../../../../../../Service/OPSServices/StoreHealthServices/reportHealthService.js";
import ReportMatrix from "./components/ReportMatrix";
import AttemptsPanel from "./components/AttemptsPanel";
import { useRetry } from "./hooks";
import SavedView from "./components/SavedView";

const fmt = (v) => new Intl.NumberFormat("en-US").format(v || 0);
const todayUtc = () => new Date().toISOString().slice(0, 10);
const ago = (iso) => {
  if (!iso) return "";
  const m = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 6e4));
  return m < 60 ? `${m} min ago` : `${(m / 60).toFixed(1)} h ago`;
};
const FILTERS = [["all", "All"], ["red", "Needs attention"], ["amber", "Waiting"], ["ok", "All saved"]];
const LEVEL = { 2: "red", 1: "amber", 0: "ok" };
const PAGE_SIZE = 50;

/** "‹ Page 1 of 3 ›" as on the inventory page; stores shown on this page in the title. */
function Pager({ page, pages, total, onPage }) {
  const from = total ? (page - 1) * PAGE_SIZE + 1 : 0, to = Math.min(page * PAGE_SIZE, total);
  return (
    <div className="rh-pager" role="navigation" aria-label="Stores pages" title={`Stores ${from}–${to} of ${total}`}>
      <button type="button" onClick={() => onPage(page - 1)} disabled={page <= 1} aria-label="Previous page">‹</button>
      <span>Page {page} of {pages}</span>
      <button type="button" onClick={() => onPage(page + 1)} disabled={page >= pages} aria-label="Next page">›</button>
    </div>
  );
}

export default function ReportHealthPage() {
  const [date, setDate] = useState(todayUtc);
  // two views of the same day: Requests (the log) and Saved data (the payload table); #saved opens the second
  const [view, setView] = useState(() => (window.location.hash === "#saved" ? "saved" : "requests"));
  const [savedReload, setSavedReload] = useState(0);
  const [savedLoading, setSavedLoading] = useState(false);
  const switchView = (v) => { setView(v); window.history.replaceState(null, "", v === "saved" ? "#saved" : window.location.pathname + window.location.search); };
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);           // { store, column }
  const [notice, setNotice] = useState(null);       // { text, error }
  const [page, setPage] = useState(1);
  const latest = useRef(0);

  const load = useCallback(async (fresh = false) => {
    const call = ++latest.current;
    setLoading(true);
    try {
      const d = await reportHealthService.getDay(date, fresh);
      if (call === latest.current) { setData(d); setError(null); }
    } catch (e) {
      if (call === latest.current) setError(e.message);
    } finally {
      if (call === latest.current) setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    load(false);
    const id = setInterval(() => { if (!document.hidden) load(false); }, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [load]);

  const counts = useMemo(() => {
    const c = { all: 0, red: 0, amber: 0, ok: 0 };
    (data ? data.stores : []).forEach((s) => { c.all += 1; c[LEVEL[s.severity]] += 1; });
    return c;
  }, [data]);

  const stores = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    return data.stores
      .filter((s) => filter === "all" || LEVEL[s.severity] === filter)
      .filter((s) => !needle || [s.storeName, s.profileId, s.market].join(" ").toLowerCase().includes(needle));
  }, [data, filter, q]);

  // 50 stores per page; back to page 1 when the filter, search or day changes
  useEffect(() => { setPage(1); }, [filter, q, date]);
  const pages = Math.max(1, Math.ceil(stores.length / PAGE_SIZE));
  const pageNo = Math.min(page, pages);
  const pageStores = useMemo(() => stores.slice((pageNo - 1) * PAGE_SIZE, pageNo * PAGE_SIZE), [stores, pageNo]);

  // hide report columns no store in the list uses
  const columns = useMemo(() => (data ? data.columns.filter((c) =>
    data.stores.some((s) => s.cells[c.name] && s.cells[c.name].state !== "none")) : []), [data]);

  // ---- retries (report level and bulk); cells being retried show "Retrying"
  const { retrying, run } = useRetry((msg) => { setNotice(msg); load(true); setSavedReload((n) => n + 1); });
  useEffect(() => { setNotice(null); }, [date]);
  const keysOf = (st) => Object.entries(st.cells).filter(([, c]) => c.retryable).map(([n]) => `${st.profileId}|${n}`);
  const retryReport = (store, column, logId) =>
    run(() => reportHealthService.retryReport(date, store.profileId, column.name, logId), [`${store.profileId}|${column.name}`],
        `Retry of ${column.name} for ${store.storeName || store.profileId}`);
  const retryStore = (store) => {
    if (!window.confirm(`Retry ${store.retryable} failed ${store.retryable === 1 ? "report" : "reports"} for ${store.storeName || store.profileId}?`)) return;
    run(() => reportHealthService.retryBulk(date, store.profileId), keysOf(store), `Retry for ${store.storeName || store.profileId}`);
  };
  const retryAll = () => {
    const n = data.summary.retryable;
    if (!window.confirm(`Retry all ${n} failed ${n === 1 ? "report" : "reports"} of ${date} for every store?`)) return;
    run(() => reportHealthService.retryBulk(date), data.stores.flatMap(keysOf), "Bulk retry");
  };

  const s = data && data.summary;
  const cards = s ? [
    { k: s.managedList ? "Managed stores" : "Stores", v: s.stores,
      sub: `${fmt(s.storesRed)} need attention${s.storesNoReports ? `, ${fmt(s.storesNoReports)} with no reports` : ""}`, c: s.storesRed ? "red" : "" },
    { k: "Reports saved", v: s.saved, sub: `of ${fmt(s.expected)} expected`, c: s.saved < s.expected ? "" : "ok" },
    { k: "Failed or stuck", v: s.failed, sub: "error, never saved, or no payload", c: s.failed ? "red" : "ok" },
    { k: "Waiting to be saved", v: s.waiting, sub: `requested under ${data.rules.stuckAfterHours} h ago; saved every ${data.rules.saveEveryHours || 2} h`, c: s.waiting ? "amber" : "" },
    { k: "Missing", v: s.missing, sub: s.notYet ? `${fmt(s.notYet)} more not requested yet today` : "usual reports not requested",
      c: s.missing ? "red" : "" },
    { k: "Can be retried", v: s.retryable, sub: data.rules.retryAllowed ? "failed, stuck or no data" : `older than ${data.rules.retryMaxAgeDays} days`, c: s.retryable ? "red" : "ok" },
  ] : [];

  return (
    <div className={`ams${loading && data ? " ams--loading" : ""}`}>
      <div className="ams-top">
        <div>
          <h1 className="ams-title">Ads report health</h1>
          <div className="ams-scope">
            {view === "saved"
              ? "Saved data per report date (the day the ad data is for), every managed store, UTC"
              : "Requests made on this day, every managed store, UTC; each request covers the 30 days before it"}
            <span className="ams-asof">{data ? `Data as of ${ago(data.generatedAt) || "now"}` : "Loading…"}</span>
          </div>
        </div>
        <div className="ams-top-right">
          <div className="ams-subbar">
            <label className="ams-dayline">
              Day (UTC)
              <input type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} />
            </label>
            <button type="button" className="ams-btn ams-btn--icon" onClick={() => (view === "saved" ? setSavedReload((n) => n + 1) : load(true))}
                    disabled={view === "saved" ? savedLoading : loading}
                    title="Reload" aria-label="Reload">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"
                   strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 11a8 8 0 1 0-2.3 5.7" /><path d="M20 4v7h-7" /></svg>
            </button>
          </div>
        </div>
      </div>

      {notice && (
        <div className={`ams-banner${notice.error ? " ams-banner--err" : ""}`} role="status">
          <span>{notice.text}</span>
          <button type="button" className="ams-banner-close" onClick={() => setNotice(null)} aria-label="Dismiss">×</button>
        </div>
      )}
      <div className="ams-tabs rh-viewtabs" role="tablist" aria-label="View">
        {[["requests", "Requests"], ["saved", "Saved data"]].map(([key, label]) => (
          <button key={key} type="button" role="tab" className="ams-tab" aria-selected={view === key} onClick={() => switchView(key)}>
            {label}
          </button>
        ))}
      </div>
      {view === "saved" ? (
        <SavedView date={date} onDate={setDate} reloadToken={savedReload} onLoading={setSavedLoading}
                   onRetry={data && data.rules.retryAllowed ? retryReport : null} retrying={retrying} />
      ) : (<>
      {error && <div className="ams-banner ams-banner--err" role="status">Could not load report health: {error}. Refresh the page; if it keeps failing, check the server log.</div>}

      {data && (
        <>
          <div className="ams-cards">
            {cards.map((c) => (
              <div className="ams-card" key={c.k}>
                <div className="ams-card-k">{c.k}</div>
                <div className={`ams-card-v${c.c ? ` ams-card-v--${c.c}` : ""}`}>{fmt(c.v)}</div>
                <div className="ams-card-s">{c.sub}</div>
              </div>
            ))}
          </div>

          <div className="ams-toolbar">
            <div className="ams-chips" role="group" aria-label="Filter">
              {FILTERS.map(([key, label]) => (
                <button key={key} type="button" className={`ams-chip ams-chip--${key}`} aria-pressed={filter === key}
                        onClick={() => setFilter(key)}>
                  {label}<span className="ams-chip-count">{counts[key]}</span>
                </button>
              ))}
            </div>
            <div className="rh-toolbar-right">
              <Pager page={pageNo} pages={pages} total={stores.length} onPage={setPage} />
              <input className="ams-search" type="search" value={q} onChange={(e) => setQ(e.target.value)}
                     placeholder="Search store, profile or market" aria-label="Search stores" />
            </div>
          </div>
          <div className="rh-legend">
            <span className="rh-key"><i className="rh-dot rh-dot--ok" />Saved, time in UTC</span>
            <span className="rh-key"><i className="rh-dot rh-dot--amber" />Requested, waiting for the next save run</span>
            <span className="rh-key"><i className="rh-dot rh-dot--red" />Failed, stuck, missing or no data</span>
            <span className="rh-key"><i className="rh-dot rh-dot--none" />0 rows (no data from Amazon) or not requested yet today</span>
            <span className="rh-key">– not used by the store</span>
            <span className="rh-key rh-key--tip">Click a cell for its log, saved data and retry.</span>
            {s.retryable > 0 && data.rules.retryAllowed && (
              <button type="button" className="ams-btn ams-btn--primary rh-bulk" onClick={retryAll}
                      disabled={data.stores.flatMap(keysOf).every((k) => retrying.has(k))}>
                Retry {fmt(s.retryable)} failed
              </button>
            )}
          </div>

          <ReportMatrix columns={columns} stores={pageStores} retrying={retrying}
                       onRetryStore={data.rules.retryAllowed ? retryStore : null}
                       onOpen={(store, column) => setOpen({ store, column })} />
          {pages > 1 && <div className="rh-pager-bottom"><Pager page={pageNo} pages={pages} total={stores.length} onPage={setPage} /></div>}
        </>
      )}
      {!data && !error && <div className="ams-empty">Loading report health…</div>}
      </>)}

      {open && (
        <AttemptsPanel date={date} store={open.store} column={open.column}
                       cell={open.store.cells[open.column.name]} onClose={() => setOpen(null)}
                       retrying={retrying.has(`${open.store.profileId}|${open.column.name}`)}
                       retryAllowed={data.rules.retryAllowed} retryMaxAgeDays={data.rules.retryMaxAgeDays}
                       onRetry={() => retryReport(open.store, open.column)} />
      )}
    </div>
  );
}
